import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { licensedSongsAPI, apiService } from '../services/api';

interface LicensedSong {
  song_name: string;
  expiry_date?: string;
  status: 'expired' | 'expiring_soon' | 'active' | 'no_expiry';
  days_until_expiry?: number;
  usage_count: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface SongsSummary {
  total_songs: number;
  expired: number;
  expiring_soon: number;
  no_expiry_date: number;
  active: number;
}

const LicensedSongsPage: React.FC = () => {
  const [songs, setSongs] = useState<LicensedSong[]>([]);
  const [summary, setSummary] = useState<SongsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSong, setSelectedSong] = useState<LicensedSong | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [adsDialogOpen, setAdsDialogOpen] = useState(false);
  const [adsForSong, setAdsForSong] = useState<{ song_name: string; count: number; ads: any[] } | null>(null);
  const [adsLoading, setAdsLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingSong, setAddingSong] = useState(false);
  const [addForm, setAddForm] = useState({
    song_name: '',
    expiry_date: null as Date | null,
    notes: ''
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    expiry_date: null as Date | null,
    notes: ''
  });

  useEffect(() => {
    loadSongs();
  }, []);

  const loadSongs = async () => {
    try {
      setLoading(true);
      const data = await licensedSongsAPI.list();
      setSongs(data.songs);
      setSummary(data.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const syncSongs = async () => {
    try {
      setSyncing(true);
      const result = await licensedSongsAPI.sync();
      setSnackbarMessage(`Sync completed: ${result.result.created} created, ${result.result.updated} updated`);
      setSnackbarOpen(true);
      
      // Reload the songs list
      await loadSongs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync songs');
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenAddDialog = () => {
    setAddForm({ song_name: '', expiry_date: null, notes: '' });
    setAddDialogOpen(true);
  };

  const handleSaveNewSong = async () => {
    const trimmedName = addForm.song_name.trim();
    if (!trimmedName) return;

    const alreadyExists = songs.some(
      (s) => s.song_name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (alreadyExists) {
      setError(`A song named "${trimmedName}" already exists.`);
      return;
    }

    try {
      setAddingSong(true);
      const requestData = {
        expiry_date: addForm.expiry_date ? format(addForm.expiry_date, 'yyyy-MM-dd') : null,
        notes: addForm.notes || null
      };

      await licensedSongsAPI.updateExpiry(trimmedName, requestData);
      setSnackbarMessage(`Song "${trimmedName}" added successfully`);
      setSnackbarOpen(true);
      setAddDialogOpen(false);
      await loadSongs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add song');
    } finally {
      setAddingSong(false);
    }
  };

  const handleEditSong = (song: LicensedSong) => {
    setSelectedSong(song);
    setEditForm({
      expiry_date: song.expiry_date ? new Date(song.expiry_date) : null,
      notes: song.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedSong) return;

    try {
      const requestData = {
        expiry_date: editForm.expiry_date ? format(editForm.expiry_date, 'yyyy-MM-dd') : null,
        notes: editForm.notes || null
      };

      const result = await licensedSongsAPI.updateExpiry(selectedSong.song_name, requestData);
      setSnackbarMessage(result.message);
      setSnackbarOpen(true);
      setEditDialogOpen(false);
      
      // Reload the songs list
      await loadSongs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update song');
    }
  };

  const handleViewAds = async (song: LicensedSong) => {
    try {
      setAdsLoading(true);
      const data = await apiService.getAdsByLicensedSong(song.song_name);
      setAdsForSong(data);
      setAdsDialogOpen(true);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ads for song');
    } finally {
      setAdsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'expired':
        return 'error';
      case 'expiring_soon':
        return 'warning';
      case 'active':
        return 'success';
      case 'no_expiry':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'expired':
        return <ErrorIcon />;
      case 'expiring_soon':
        return <WarningIcon />;
      case 'active':
        return <CheckCircleIcon />;
      case 'no_expiry':
        return <HelpIcon />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'expired':
        return 'Expired';
      case 'expiring_soon':
        return 'Expiring Soon';
      case 'active':
        return 'Active';
      case 'no_expiry':
        return 'No Expiry Date';
      default:
        return status;
    }
  };

  const filteredSongs = filterStatus === 'all' 
    ? songs 
    : songs.filter(song => song.status === filterStatus);

  const urgentSongs = songs.filter(song => 
    song.status === 'expired' || song.status === 'expiring_soon'
  );

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Licensed Songs Management
        </Typography>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading licensed songs...
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Licensed Songs Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Add Song
            </Button>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={syncSongs}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync from Ads'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Songs
                  </Typography>
                  <Typography variant="h5">
                    {summary.total_songs}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#ffebee' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Expired
                  </Typography>
                  <Typography variant="h5" color="error">
                    {summary.expired}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Expiring Soon
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {summary.expiring_soon}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#e8f5e8' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {summary.active}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    No Expiry Date
                  </Typography>
                  <Typography variant="h5">
                    {summary.no_expiry_date}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Urgent Songs Alert */}
        {urgentSongs.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>
              {urgentSongs.length} Song{urgentSongs.length === 1 ? '' : 's'} Need{urgentSongs.length === 1 ? 's' : ''} Attention
            </AlertTitle>
            {urgentSongs.slice(0, 3).map((song, index) => (
              <Typography key={song.song_name} variant="body2">
                • <strong>{song.song_name}</strong> - {song.status === 'expired' ? 'Expired' : `Expires in ${song.days_until_expiry} days`}
              </Typography>
            ))}
            {urgentSongs.length > 3 && (
              <Typography variant="body2">
                ... and {urgentSongs.length - 3} more
              </Typography>
            )}
          </Alert>
        )}

        {/* Filter Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FilterIcon />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="all">All Songs</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="expiring_soon">Expiring Soon</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="no_expiry">No Expiry Date</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredSongs.length} of {songs.length} songs
          </Typography>
        </Box>

        {/* Songs Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Song Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Days Until Expiry</TableCell>
                  <TableCell>Usage Count</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSongs.map((song) => (
                  <TableRow key={song.song_name}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {song.song_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(song.status) || undefined}
                        label={getStatusLabel(song.status)}
                        color={getStatusColor(song.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {song.expiry_date ? (
                        <Typography variant="body2">
                          {format(new Date(song.expiry_date), 'MMM dd, yyyy')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Not set
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {song.days_until_expiry !== null && song.days_until_expiry !== undefined ? (
                        <Typography 
                          variant="body2"
                          color={song.days_until_expiry < 0 ? 'error' : song.days_until_expiry <= 14 ? 'warning.main' : 'textPrimary'}
                        >
                          {song.days_until_expiry < 0 
                            ? `${Math.abs(song.days_until_expiry)} days ago`
                            : `${song.days_until_expiry} days`
                          }
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={song.usage_count} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={song.notes || 'No notes'}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 200, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {song.notes || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditSong(song)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <Button 
                        size="small" 
                        variant="text" 
                        onClick={() => handleViewAds(song)}
                        sx={{ ml: 1 }}
                      >
                        View Ads
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit Expiry Information for "{selectedSong?.song_name}"
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <DatePicker
                label="Expiry Date"
                value={editForm.expiry_date}
                onChange={(newValue) => setEditForm({ ...editForm, expiry_date: newValue })}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                label="Notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="Additional notes about this song license..."
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Song Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Licensed Song</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Song Name"
                value={addForm.song_name}
                onChange={(e) => setAddForm({ ...addForm, song_name: e.target.value })}
                placeholder="e.g. Shape of You - Ed Sheeran"
                fullWidth
                autoFocus
                required
              />
              <DatePicker
                label="Expiry Date (optional)"
                value={addForm.expiry_date}
                onChange={(newValue) => setAddForm({ ...addForm, expiry_date: newValue })}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                label="Notes (optional)"
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="License details, source, restrictions..."
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)} disabled={addingSong}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNewSong}
              variant="contained"
              disabled={!addForm.song_name.trim() || addingSong}
            >
              {addingSong ? 'Adding...' : 'Add Song'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />

        {/* Ads Dialog */}
        <Dialog open={adsDialogOpen} onClose={() => setAdsDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Ads using "{adsForSong?.song_name}"
          </DialogTitle>
          <DialogContent>
            {adsLoading ? (
              <LinearProgress />
            ) : adsForSong && adsForSong.ads.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ad ID</TableCell>
                    <TableCell>Production</TableCell>
                    <TableCell>Creative #</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adsForSong.ads.map((ad) => (
                    <TableRow key={ad.ad_id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{ad.ad_id}</TableCell>
                      <TableCell>{ad.production_id}</TableCell>
                      <TableCell>{ad.creative_number}</TableCell>
                      <TableCell>{ad.name_en}</TableCell>
                      <TableCell>{new Date(ad.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="textSecondary">No ads found for this song.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default LicensedSongsPage;
